import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Phone, MessageCircle, Mail, MapPin, Clock, ArrowRight, ShieldCheck, Award, Scale, FileText,
  Building2, Home as HomeIcon, Star, ChevronDown, Menu, X, CheckCircle2, TrendingUp,
  Briefcase, Landmark, KeyRound, ClipboardCheck, ArrowUp, Facebook, Instagram, Youtube, Twitter,
  Loader2, AlertTriangle, RefreshCw,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icons available to CMS `icon` field. Everything else falls back to FileText.
const ICON_MAP = {
  FileText, Home: HomeIcon, Building2, ClipboardCheck, Landmark, Briefcase, KeyRound, ShieldCheck,
  Scale, Award, Star, TrendingUp, Globe: MapPin,
};

// ============================================================
// Header
// ============================================================
function Header({ nav, brand, primaryPhone, primaryPhoneDisplay }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);
  const line1 = brand?.line1 || "SHREE SAMARTH";
  const line2 = brand?.line2 || "Consultancy";
  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur-md shadow-md" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 sm:h-20">
        <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
          <img src="/logo.png" alt={line1} className="h-10 w-10 rounded-md object-contain bg-white p-0.5 shadow-sm" />
          <div className="leading-tight">
            <div className={`font-display font-black text-sm tracking-tight ${scrolled ? "text-slate-900" : "text-white"}`} data-testid="header-brand-line1">{line1}</div>
            <div className={`text-[9px] uppercase tracking-widest font-semibold ${scrolled ? "text-[#C89B4A]" : "text-[#F5D77A]"}`} data-testid="header-brand-line2">{line2}</div>
          </div>
        </Link>
        <nav className="hidden lg:flex items-center gap-1">
          {nav.map((n) => (
            <a key={n.href} href={n.href} className={`px-3 py-2 rounded-md text-[13px] font-semibold transition-colors ${scrolled ? "text-slate-700 hover:text-[#0B2E5C] hover:bg-slate-100" : "text-white/90 hover:text-white hover:bg-white/10"}`}>{n.label}</a>
          ))}
          <Link to="/login" className={`px-3 py-2 rounded-md text-[13px] font-semibold transition-colors ${scrolled ? "text-[#0B2E5C] hover:bg-slate-100 border border-[#0B2E5C]/20" : "text-white/90 hover:text-white hover:bg-white/10 border border-white/30"}`} data-testid="header-staff-login">Staff Login</Link>
          {primaryPhone && (
            <a href={`tel:${primaryPhone}`} className="ml-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#C89B4A] hover:bg-[#B08536] text-white font-semibold text-sm shadow-sm transition" data-testid="header-call-btn">
              <Phone className="h-4 w-4" /> Call Now
            </a>
          )}
        </nav>
        <button onClick={() => setOpen(!open)} className={`lg:hidden ${scrolled ? "text-slate-800" : "text-white"}`} data-testid="mobile-menu-toggle">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200 shadow-lg">
          <div className="px-4 py-3 space-y-1">
            {nav.map((n) => (
              <a key={n.href} href={n.href} onClick={() => setOpen(false)} className="block px-3 py-2 rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-100">{n.label}</a>
            ))}
            <Link to="/login" onClick={() => setOpen(false)} className="block px-3 py-2 rounded-md text-sm font-semibold text-[#0B2E5C] hover:bg-slate-100 border border-[#0B2E5C]/20">Staff Login</Link>
            {primaryPhone && (
              <a href={`tel:${primaryPhone}`} className="block mt-2 px-3 py-2.5 rounded-md bg-[#C89B4A] text-white font-semibold text-sm text-center">Call {primaryPhoneDisplay}</a>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

// ============================================================
// Hero
// ============================================================
function Hero({ office, brand }) {
  const phoneTel = office?.phone_tel || brand?.phone_tel;
  const whatsapp = office?.whatsapp || brand?.whatsapp;
  const line1 = brand?.line1 || "SHREE SAMARTH";
  const line2 = brand?.line2 || "CONSULTANCY";
  const tagline = brand?.tagline || "Legal Documentation · Property Registration · Government Services";
  const description = brand?.description ||
    "Professional legal documentation and registration services with complete transparency, accuracy, and government compliance. Trusted by 10,000+ satisfied clients across Palghar & Vasai-Virar.";
  return (
    <section id="home" className="relative min-h-[92vh] flex items-center pt-20" data-testid="hero-section">
      <div className="absolute inset-0" style={{
        backgroundImage: "linear-gradient(135deg, rgba(11,46,92,0.92) 0%, rgba(24,106,101,0.85) 100%), url('https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1920&q=80')",
        backgroundSize: "cover", backgroundPosition: "center",
      }} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/20 mb-5">
            <span className="h-2 w-2 rounded-full bg-[#F5D77A] animate-pulse" />
            <span className="text-xs text-white font-semibold tracking-widest uppercase">A Sister Concern of Property Hubs Group</span>
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight" data-testid="hero-office-name">
            {line1}<br /><span className="text-[#F5D77A]">{line2.toUpperCase()}</span>
          </h1>
          <p className="mt-4 text-lg sm:text-xl text-white/90 font-semibold" data-testid="hero-tagline">
            {tagline}
          </p>
          <p className="mt-3 text-sm sm:text-base text-white/80 max-w-xl leading-relaxed">
            {description}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {phoneTel && (
              <a href={`tel:${phoneTel}`} data-testid="hero-call-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#C89B4A] hover:bg-[#B08536] text-white font-bold shadow-lg transition"><Phone className="h-5 w-5" /> Call Now</a>
            )}
            {whatsapp && (
              <a href={`https://wa.me/${whatsapp}?text=Hello%2C%20I%20would%20like%20to%20know%20more%20about%20your%20services`} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#25D366] hover:bg-[#1FB258] text-white font-bold shadow-lg transition"><MessageCircle className="h-5 w-5" /> WhatsApp</a>
            )}
            <a href="#contact" data-testid="hero-book-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-white/10 hover:bg-white/20 border border-white/40 text-white font-bold backdrop-blur transition">Book Consultation <ArrowRight className="h-5 w-5" /></a>
          </div>
          <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
            {[["10K+", "Happy Clients"], ["15+", "Years Exp."], ["25K+", "Documents"]].map(([n, l]) => (
              <div key={l} className="text-center border-l-2 border-[#F5D77A]/50 pl-3">
                <div className="font-display font-black text-2xl text-white">{n}</div>
                <div className="text-[10px] uppercase tracking-widest text-white/70 font-semibold">{l}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="hidden lg:block">
          <div className="relative">
            <div className="absolute -top-4 -left-4 w-full h-full border-2 border-[#F5D77A]/40 rounded-lg" />
            <img src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=800&q=80" alt="Legal Documentation" className="rounded-lg shadow-2xl relative" />
            <div className="absolute -bottom-6 -right-6 bg-white rounded-lg shadow-xl p-4 flex items-center gap-3 max-w-xs">
              <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <ShieldCheck className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <div className="font-display font-bold text-slate-900 text-sm">Government Approved</div>
                <div className="text-xs text-slate-500">100% legal & compliant process</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// About
// ============================================================
function About() {
  return (
    <section id="about" className="py-20 bg-white" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#C89B4A] font-bold mb-3">About Us</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-[#0B2E5C] leading-tight">
            Your Trusted Partner for <span className="text-[#186A65]">Legal & Property</span> Solutions
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed">
            Shree Samarth Consultancy is the <strong>Legal Documentation & Government Services Division</strong> of Property Hubs Group — a trusted real estate company specializing in buying, selling, renting and property investments. Together, both companies provide complete legal and real estate solutions under one trusted organization.
          </p>
          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            {[
              ["Experienced Team", "15+ years serving Palghar & Vasai-Virar"],
              ["Transparent Process", "No hidden charges, clear documentation"],
              ["Fast Service", "Registration in as little as 24 hours"],
              ["Government Compliant", "100% approved & audited procedures"],
            ].map(([t, d]) => (
              <div key={t} className="flex gap-3">
                <CheckCircle2 className="h-5 w-5 text-[#186A65] shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold text-slate-900 text-sm">{t}</div>
                  <div className="text-xs text-slate-500">{d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <img src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=600&q=80" alt="Office" className="rounded-lg shadow-lg h-56 w-full object-cover" />
          <img src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=600&q=80" alt="Documentation" className="rounded-lg shadow-lg h-56 w-full object-cover mt-8" />
          <img src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=600&q=80" alt="Consultation" className="rounded-lg shadow-lg h-40 w-full object-cover" />
          <img src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=600&q=80" alt="Property" className="rounded-lg shadow-lg h-40 w-full object-cover" />
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Dynamic Category Section (fully CMS-driven)
// ============================================================
function DynamicCategorySection({ cat, services, onOpen, isDark }) {
  if (!services.length) return null;
  const grid = "grid sm:grid-cols-2 lg:grid-cols-4 gap-4";
  return (
    <section
      id={cat.slug}
      className={`py-20 ${isDark ? "bg-gradient-to-br from-[#0B2E5C] to-[#186A65] text-white" : "bg-slate-50"} border-t border-slate-200`}
      data-testid={`cms-cat-${cat.slug}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className={`text-xs uppercase tracking-[0.25em] font-bold mb-3 ${isDark ? "text-[#F5D77A]" : "text-[#C89B4A]"}`}>{cat.name}</div>
          {cat.tagline && <h2 className={`font-display font-black text-3xl sm:text-4xl ${isDark ? "text-white" : "text-[#0B2E5C]"}`}>{cat.tagline}</h2>}
          {cat.description && <p className={`mt-3 ${isDark ? "text-white/80" : "text-slate-600"}`}>{cat.description}</p>}
        </div>
        <div className={grid}>
          {services.map((s) => {
            const Icon = ICON_MAP[s.icon] || FileText;
            return (
              <button
                key={s.id}
                onClick={() => onOpen(s)}
                className={`text-left group rounded-lg p-5 transition-all duration-300 hover:-translate-y-1 ${isDark ? "bg-white/5 backdrop-blur border border-white/10 hover:bg-white/10 hover:border-[#F5D77A]/40" : "bg-white border border-slate-200 hover:border-[#C89B4A] hover:shadow-lg"}`}
                data-testid={`cms-svc-${s.slug || s.id}`}
              >
                <div className={`h-12 w-12 rounded-md flex items-center justify-center mb-3 group-hover:scale-110 transition ${isDark ? "bg-[#C89B4A] text-white" : "bg-gradient-to-br from-[#0B2E5C] to-[#186A65] text-white"}`}>
                  <Icon className="h-6 w-6" />
                </div>
                <div className={`font-display font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{s.name}</div>
                {s.description && <div className={`text-xs mt-1 line-clamp-2 ${isDark ? "text-white/70" : "text-slate-500"}`}>{s.description}</div>}
                {s.documents?.length > 0 && (
                  <div className={`text-[10px] mt-2 font-semibold ${isDark ? "text-[#F5D77A]" : "text-[#C89B4A]"}`}>
                    {s.documents.length} required document{s.documents.length === 1 ? "" : "s"}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Service Detail Modal
// ============================================================
function ServiceDetailModal({ svc, primaryOffice, brand, onClose }) {
  if (!svc) return null;
  // Effective contact: service can override company defaults; only shown if flag is on.
  const ec = svc.effective_contact || {};
  const showSvcContact = !!ec.show;
  const wa = (showSvcContact && ec.whatsapp) || primaryOffice?.whatsapp || brand?.whatsapp || "";
  const tel = (showSvcContact && ec.primary_phone) || primaryOffice?.phone_tel || brand?.phone_tel || "";
  const alt = showSvcContact ? (ec.alt_phone || "") : "";
  const email = (showSvcContact && ec.email) || primaryOffice?.email || brand?.email || "";
  const waMsg = encodeURIComponent(`Hello, I need information about ${svc.name} and its required documents.`);
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="service-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-gradient-to-r from-[#0B2E5C] to-[#186A65] text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="h-6 w-6" />
            <div className="font-display font-black text-lg tracking-tight" data-testid="modal-service-name">{svc.name}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          {svc.description && <p className="text-sm text-slate-700">{svc.description}</p>}
          {svc.special_instructions && (
            <div className="bg-amber-50 border-l-4 border-amber-400 p-3 text-sm text-amber-900">
              <strong>Special Instructions:</strong> {svc.special_instructions}
            </div>
          )}
          {svc.documents && svc.documents.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-widest text-[#C89B4A] font-bold mb-2">Required Documents</div>
              <ul className="space-y-2">
                {svc.documents.map((d) => (
                  <li key={d.id} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className={`h-4 w-4 mt-0.5 flex-shrink-0 ${d.mandatory ? "text-red-600" : "text-slate-400"}`} />
                    <div className="flex-1">
                      <span className={d.mandatory ? "font-semibold text-slate-900" : "text-slate-700"}>{d.name}</span>
                      <span className={`ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded ${d.mandatory ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}>{d.mandatory ? "MANDATORY" : "OPTIONAL"}</span>
                      {d.note && <div className="text-xs text-slate-500 italic mt-0.5">{d.note}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {showSvcContact && (ec.primary_phone || ec.alt_phone || ec.email) && (
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-1" data-testid="modal-service-contact">
              <div className="text-xs uppercase tracking-widest text-[#C89B4A] font-bold mb-1">Contact for this service</div>
              {ec.primary_phone && <div className="text-sm text-slate-700"><strong>Primary:</strong> <a href={`tel:${ec.primary_phone}`} className="text-[#0B2E5C] hover:underline" data-testid="modal-primary-phone">{ec.primary_phone}</a></div>}
              {alt && <div className="text-sm text-slate-700"><strong>Alternate:</strong> <a href={`tel:${alt}`} className="text-[#0B2E5C] hover:underline" data-testid="modal-alt-phone">{alt}</a></div>}
              {ec.email && <div className="text-sm text-slate-700"><strong>Email:</strong> <a href={`mailto:${ec.email}`} className="text-[#0B2E5C] hover:underline break-all" data-testid="modal-email">{ec.email}</a></div>}
            </div>
          )}
          <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-200">
            {wa && <a href={`https://wa.me/${wa}?text=${waMsg}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold" data-testid="modal-wa"><MessageCircle className="h-4 w-4" /> WhatsApp</a>}
            {tel && <a href={`tel:${tel}`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#0B2E5C] hover:bg-[#08234a] text-white text-sm font-semibold" data-testid="modal-call"><Phone className="h-4 w-4" /> Call Now</a>}
            <a href="#contact" onClick={onClose} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#C89B4A] hover:bg-[#B08536] text-white text-sm font-semibold" data-testid="modal-enquire">Enquire <ArrowRight className="h-4 w-4" /></a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Why Choose Us
// ============================================================
function WhyUs() {
  const items = [
    ["Government Approved", ShieldCheck], ["Experienced Legal Experts", Scale],
    ["Property Documentation", FileText], ["Trusted Consultancy", Award],
    ["Transparent Pricing", CheckCircle2], ["Home Visit Facility", HomeIcon],
    ["Fast Registration", TrendingUp], ["Complete Legal Support", Briefcase],
    ["Customer Satisfaction", Star],
  ];
  return (
    <section id="why" className="py-20 bg-slate-50" data-testid="why-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-xs uppercase tracking-[0.25em] text-[#C89B4A] font-bold mb-3">Why Choose Us</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl text-[#0B2E5C]">Trust Built Over 15 Years</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map(([label, Icon]) => (
            <div key={label} className="bg-white rounded-lg p-5 border-l-4 border-[#C89B4A] flex items-center gap-4 shadow-sm hover:shadow-md transition">
              <div className="h-12 w-12 rounded-md bg-gradient-to-br from-[#0B2E5C] to-[#186A65] text-white flex items-center justify-center">
                <Icon className="h-6 w-6" />
              </div>
              <div className="font-display font-bold text-slate-900">{label}</div>
            </div>
          ))}
        </div>
        <div className="mt-16">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-[0.25em] text-[#C89B4A] font-bold mb-3">Our Process</div>
            <h3 className="font-display font-black text-2xl sm:text-3xl text-[#0B2E5C]">How We Work</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {["Free Consultation", "Document Verification", "Draft Preparation", "Registration", "Document Delivery"].map((step, i) => (
              <div key={step} className="text-center relative">
                <div className="h-14 w-14 mx-auto rounded-full bg-white border-2 border-[#C89B4A] text-[#0B2E5C] flex items-center justify-center font-display font-black text-lg shadow-md">{i + 1}</div>
                <div className="mt-3 font-bold text-sm text-slate-800">{step}</div>
                {i < 4 && <div className="hidden md:block absolute top-7 -right-2 w-4 h-0.5 bg-[#C89B4A]" />}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Testimonials
// ============================================================
const TESTIMONIALS = [
  { name: "Rajesh Sharma", role: "Property Buyer, Naigaon", text: "Bought my first flat with their help. Registration was done in just 2 days. Very professional team!", rating: 5 },
  { name: "Priya Deshmukh", role: "Business Owner, Vasai", text: "Got my LLP registration and GST done together. Transparent pricing, no hidden fees.", rating: 5 },
  { name: "Amit Patil", role: "Investor, Palghar", text: "Best legal consultants in Palghar. I have referred 5+ friends. They handle everything end-to-end.", rating: 5 },
  { name: "Sunita More", role: "Landlord, Naigaon East", text: "Rent agreement registered at home with home-visit facility. Extremely convenient!", rating: 5 },
  { name: "Deepak Joshi", role: "Senior Citizen, Palghar", text: "Received my Senior Citizen certificate and Domicile within a week. Respectful staff.", rating: 5 },
  { name: "Neha Kulkarni", role: "First-time Buyer, Vasai", text: "Their expert legal verification saved me from a bad deal. Grateful for the honest advice.", rating: 5 },
];

function Testimonials() {
  return (
    <section className="py-20 bg-white" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-xs uppercase tracking-[0.25em] text-[#C89B4A] font-bold mb-3">Testimonials</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl text-[#0B2E5C]">What Our Clients Say</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className="bg-slate-50 rounded-lg p-6 border border-slate-200 hover:border-[#C89B4A] transition" data-testid={`testimonial-${i}`}>
              <div className="flex gap-0.5 mb-3">
                {Array.from({ length: t.rating }).map((_, k) => <Star key={k} className="h-4 w-4 fill-[#C89B4A] text-[#C89B4A]" />)}
              </div>
              <p className="text-slate-700 leading-relaxed italic text-sm">"{t.text}"</p>
              <div className="mt-5 pt-4 border-t border-slate-200 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#0B2E5C] to-[#186A65] text-white font-bold flex items-center justify-center">{t.name[0]}</div>
                <div>
                  <div className="font-display font-bold text-slate-900 text-sm">{t.name}</div>
                  <div className="text-[11px] text-slate-500">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// FAQ
// ============================================================
function FAQ({ primaryPhoneDisplay }) {
  const FAQS = [
    ["How long does property registration take?", "Standard registration is completed within 24-48 hours if all documents are in order. Complex cases may take 3-5 working days."],
    ["What documents are required for rent agreement registration?", "Landlord & tenant ID proof (Aadhaar/PAN), address proof, 2 witness IDs, property tax receipt, and the agreement draft."],
    ["How much is stamp duty in Maharashtra?", "Stamp duty varies by property type and location (typically 5-6% of agreement value). Contact us for exact calculation based on your case."],
    ["Can registration be done at home?", "Yes, we offer home-visit facility for Leave & License and Rent Agreement registration across Palghar, Vasai and Virar."],
    ["Do you provide online registration?", "Yes, for eligible services like Leave & License, e-Registration is available. We handle end-to-end online submission."],
    ["Can you help with PAN card and Passport?", "Yes, we assist with new PAN, corrections, reprint, and both fresh & renewal Passport applications with appointment booking."],
    ["Do you handle Society and Trust registration?", "Yes, we handle Cooperative Society, Charitable Trust, Section-8 Company and NGO registrations end-to-end."],
    ["What is the cost of your consultancy?", "Consultation is free. Service charges depend on the specific service — always quoted upfront with no hidden fees."],
    ["Are you available on Sundays?", "We are open Monday to Saturday, 10:00 AM to 8:00 PM. Sunday visits are by appointment only."],
    ["How can I book a consultation?", `Call us at ${primaryPhoneDisplay || "our office"}, WhatsApp us, or fill the contact form below. We respond within 2 hours during business hours.`],
  ];
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section id="faq" className="py-20 bg-slate-50" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-[0.25em] text-[#C89B4A] font-bold mb-3">FAQ</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl text-[#0B2E5C]">Frequently Asked Questions</h2>
        </div>
        <div className="space-y-3">
          {FAQS.map(([q, a], i) => (
            <div key={i} className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid={`faq-${i}`}>
              <button onClick={() => setOpenIdx(openIdx === i ? -1 : i)} className="w-full text-left px-5 py-4 flex items-center justify-between font-display font-bold text-slate-900 hover:bg-slate-50 transition">
                <span className="pr-4">{q}</span>
                <ChevronDown className={`h-5 w-5 text-[#C89B4A] shrink-0 transition-transform ${openIdx === i ? "rotate-180" : ""}`} />
              </button>
              {openIdx === i && <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-3">{a}</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Contact
// ============================================================
function Contact({ offices, categories, services }) {
  const [form, setForm] = useState({ name: "", mobile: "", email: "", service: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mobile.trim()) {
      toast.error("Please fill in your name and mobile number");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Thank you! We'll get back to you within 2 hours.");
      setForm({ name: "", mobile: "", email: "", service: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit. Please call us directly.");
    } finally { setBusy(false); }
  };
  const primary = offices[0];
  const mapUrl = primary?.maps_url
    || (primary?.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(primary.address)}` : null);
  const servicesByCat = useMemo(() => {
    const m = {};
    for (const s of services) (m[s.category_id] ||= []).push(s);
    return m;
  }, [services]);
  return (
    <section id="contact" className="py-20 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-xs uppercase tracking-[0.25em] text-[#C89B4A] font-bold mb-3">Contact Us</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl text-[#0B2E5C]">Get In Touch</h2>
          <p className="mt-3 text-slate-600">Free consultation. Response within 2 hours during business hours.</p>
        </div>
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="space-y-4">
            {offices.map((o, idx) => (
              <div key={o.id} className="bg-slate-50 rounded-lg border-l-4 border-[#C89B4A] p-5 space-y-3" data-testid={`office-card-${idx}`}>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#0B2E5C] bg-[#F5D77A] rounded-sm px-2 py-0.5" data-testid={`office-name-${idx}`}>{o.name}</span>
                  {o.is_head && <span className="text-[9px] uppercase tracking-widest font-bold text-emerald-700 bg-emerald-100 rounded-sm px-1.5 py-0.5">HEAD OFFICE</span>}
                </div>
                {o.address && (
                  <div className="flex items-start gap-3" data-testid={`office-address-${idx}`}>
                    <div className="h-9 w-9 rounded-md bg-gradient-to-br from-[#0B2E5C] to-[#186A65] text-white flex items-center justify-center shrink-0">
                      <MapPin className="h-4 w-4" />
                    </div>
                    <div className="text-sm text-slate-700 leading-relaxed">{o.address}</div>
                  </div>
                )}
                {(o.phone_tel || o.phone_display) && (
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-md bg-gradient-to-br from-[#0B2E5C] to-[#186A65] text-white flex items-center justify-center shrink-0">
                      <Phone className="h-4 w-4" />
                    </div>
                    <a href={`tel:${o.phone_tel || o.phone_display}`} className="text-sm text-slate-700 hover:text-[#0B2E5C] font-mono">{o.phone_display || o.phone_tel}</a>
                    {o.whatsapp && (
                      <a
                        href={`https://wa.me/${o.whatsapp}?text=Hello%2C%20I%20would%20like%20to%20know%20more%20about%20your%20services`}
                        target="_blank" rel="noreferrer"
                        className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded bg-[#25D366] hover:bg-[#1FB258] text-white text-[11px] font-bold"
                        data-testid={`office-whatsapp-${idx}`}
                      >
                        <MessageCircle className="h-3 w-3" /> WhatsApp
                      </a>
                    )}
                  </div>
                )}
                {o.email && (
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-md bg-gradient-to-br from-[#0B2E5C] to-[#186A65] text-white flex items-center justify-center shrink-0">
                      <Mail className="h-4 w-4" />
                    </div>
                    <a href={`mailto:${o.email}`} className="text-sm text-slate-700 hover:text-[#0B2E5C] break-all">{o.email}</a>
                  </div>
                )}
                {o.office_timings && (
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-md bg-gradient-to-br from-[#0B2E5C] to-[#186A65] text-white flex items-center justify-center shrink-0">
                      <Clock className="h-4 w-4" />
                    </div>
                    <div className="text-sm text-slate-600">{o.office_timings}</div>
                  </div>
                )}
              </div>
            ))}
            {mapUrl && (
              <div className="rounded-lg overflow-hidden border-2 border-slate-200 h-64">
                <iframe
                  title="location-map"
                  src={`https://www.google.com/maps?q=${encodeURIComponent(primary?.address || primary?.name || "")}&output=embed`}
                  width="100%" height="100%" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                  style={{ border: 0 }}
                />
              </div>
            )}
          </div>
          <form onSubmit={submit} className="bg-slate-50 rounded-lg p-6 border border-slate-200 space-y-4" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-slate-700">Full Name *</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5 w-full h-11 px-3 rounded-md border border-slate-300 bg-white focus:border-[#C89B4A] focus:ring-2 focus:ring-[#C89B4A]/20 outline-none text-sm" data-testid="contact-name" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-slate-700">Mobile *</label>
                <input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })} required pattern="[0-9]{10}" className="mt-1.5 w-full h-11 px-3 rounded-md border border-slate-300 bg-white focus:border-[#C89B4A] focus:ring-2 focus:ring-[#C89B4A]/20 outline-none text-sm font-mono" data-testid="contact-mobile" />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-slate-700">Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 w-full h-11 px-3 rounded-md border border-slate-300 bg-white focus:border-[#C89B4A] focus:ring-2 focus:ring-[#C89B4A]/20 outline-none text-sm" data-testid="contact-email" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-slate-700">Service Interested In</label>
              <select value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="mt-1.5 w-full h-11 px-3 rounded-md border border-slate-300 bg-white focus:border-[#C89B4A] focus:ring-2 focus:ring-[#C89B4A]/20 outline-none text-sm" data-testid="contact-service">
                <option value="">-- Select --</option>
                {categories.map((c) => {
                  const list = servicesByCat[c.id] || [];
                  if (!list.length) return null;
                  return (
                    <optgroup key={c.id} label={c.name}>
                      {list.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                    </optgroup>
                  );
                })}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-slate-700">Message</label>
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} className="mt-1.5 w-full px-3 py-2 rounded-md border border-slate-300 bg-white focus:border-[#C89B4A] focus:ring-2 focus:ring-[#C89B4A]/20 outline-none text-sm resize-none" data-testid="contact-message" />
            </div>
            <button type="submit" disabled={busy} className="w-full h-12 rounded-md bg-[#C89B4A] hover:bg-[#B08536] text-white font-display font-bold shadow-md transition disabled:opacity-60" data-testid="contact-submit">
              {busy ? "Sending…" : "Send Message"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Footer
// ============================================================
function Footer({ offices, nav, brand }) {
  const line1 = brand?.line1 || "SHREE SAMARTH";
  const line2 = brand?.line2 || "Consultancy";
  const desc = brand?.description || "Your trusted partner for legal documentation, property registration and government services in Palghar & Vasai-Virar. A sister concern of Property Hubs Group.";
  const copyright = brand?.copyright || `© ${new Date().getFullYear()} ${line1} ${line2}. All rights reserved.`;
  return (
    <footer className="bg-[#0B2E5C] text-white/85 pt-16 pb-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-4 gap-8">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2.5 mb-4">
            <img src="/logo.png" alt={line1} className="h-12 w-12 rounded-md object-contain bg-white p-1" />
            <div>
              <div className="font-display font-black text-white" data-testid="footer-brand-line1">{line1}</div>
              <div className="text-[10px] uppercase tracking-widest text-[#F5D77A]" data-testid="footer-brand-line2">{line2}</div>
            </div>
          </div>
          <p className="text-sm leading-relaxed max-w-md">{desc}</p>
          <div className="mt-5 flex gap-2">
            {[Facebook, Instagram, Youtube, Twitter].map((I, i) => (
              <a key={i} href="#" className="h-9 w-9 rounded-md bg-white/10 hover:bg-[#C89B4A] flex items-center justify-center transition"><I className="h-4 w-4" /></a>
            ))}
          </div>
        </div>
        <div>
          <div className="font-display font-bold text-white text-sm mb-4 uppercase tracking-wider">Quick Links</div>
          <ul className="space-y-2 text-sm">
            {nav.map((n) => <li key={n.href}><a href={n.href} className="hover:text-[#F5D77A]">{n.label}</a></li>)}
          </ul>
        </div>
        <div>
          <div className="font-display font-bold text-white text-sm mb-4 uppercase tracking-wider">Contact</div>
          <ul className="space-y-3 text-sm">
            {offices.map((o, idx) => (
              <li key={o.id} className="space-y-1 pb-2 border-b border-white/10 last:border-0" data-testid={`footer-office-${idx}`}>
                <div className="text-[10px] uppercase tracking-widest text-[#F5D77A] font-bold">{o.name}</div>
                {o.address && <div className="flex items-start gap-2"><MapPin className="h-4 w-4 shrink-0 mt-0.5 text-[#F5D77A]" /><span>{o.address}</span></div>}
                {(o.phone_tel || o.phone_display) && <a href={`tel:${o.phone_tel || o.phone_display}`} className="flex items-center gap-2 hover:text-[#F5D77A]"><Phone className="h-4 w-4 text-[#F5D77A]" /> {o.phone_display || o.phone_tel}</a>}
                {o.email && <a href={`mailto:${o.email}`} className="flex items-center gap-2 hover:text-[#F5D77A] break-all"><Mail className="h-4 w-4 shrink-0 text-[#F5D77A]" /> {o.email}</a>}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
        <div>© {new Date().getFullYear()} {brand?.line1 || "SHREE SAMARTH"} {brand?.line2 || "CONSULTANCY"}. All rights reserved.</div>
        <div className="flex gap-4"><a href="#" className="hover:text-[#F5D77A]">Privacy Policy</a><a href="#" className="hover:text-[#F5D77A]">Terms & Conditions</a></div>
      </div>
    </footer>
  );
}

// ============================================================
// Floating Buttons
// ============================================================
function FloatingButtons({ primaryOffice }) {
  const [top, setTop] = useState(false);
  useEffect(() => {
    const on = () => setTop(window.scrollY > 400);
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);
  const phone = primaryOffice?.phone_tel;
  const whatsapp = primaryOffice?.whatsapp;
  return (
    <div className="fixed bottom-5 right-5 z-40 flex flex-col gap-2.5">
      {top && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="h-11 w-11 rounded-full bg-[#0B2E5C] hover:bg-[#0B2E5C]/90 text-white shadow-lg flex items-center justify-center" title="Back to Top" data-testid="back-to-top-btn">
          <ArrowUp className="h-5 w-5" />
        </button>
      )}
      {whatsapp && (
        <a href={`https://wa.me/${whatsapp}?text=Hello%2C%20I%20would%20like%20to%20know%20more`} target="_blank" rel="noreferrer" className="h-14 w-14 rounded-full bg-[#25D366] hover:bg-[#1FB258] text-white shadow-xl flex items-center justify-center animate-pulse" title="WhatsApp" data-testid="float-whatsapp-btn">
          <MessageCircle className="h-7 w-7" />
        </a>
      )}
      {phone && (
        <a href={`tel:${phone}`} className="h-14 w-14 rounded-full bg-[#C89B4A] hover:bg-[#B08536] text-white shadow-xl flex items-center justify-center" title="Call" data-testid="float-call-btn">
          <Phone className="h-6 w-6" />
        </a>
      )}
    </div>
  );
}

// ============================================================
// Loading & Error UI
// ============================================================
function LoadingSplash() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0B2E5C] to-[#186A65]" data-testid="public-loading">
      <div className="text-center text-white">
        <Loader2 className="h-10 w-10 animate-spin mx-auto mb-4 text-[#F5D77A]" />
        <div className="font-display font-bold text-lg">Loading Shree Samarth Consultancy…</div>
      </div>
    </div>
  );
}

function ErrorSplash({ onRetry, message }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6" data-testid="public-error">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg border border-slate-200 p-8 text-center">
        <div className="h-14 w-14 rounded-full bg-red-100 text-red-600 mx-auto flex items-center justify-center mb-4">
          <AlertTriangle className="h-7 w-7" />
        </div>
        <div className="font-display font-black text-xl text-slate-900 mb-2">Unable to load website content</div>
        <div className="text-sm text-slate-600 mb-6">{message || "Please check your internet connection and try again."}</div>
        <button onClick={onRetry} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#0B2E5C] hover:bg-[#08234a] text-white font-semibold text-sm" data-testid="public-retry">
          <RefreshCw className="h-4 w-4" /> Retry
        </button>
      </div>
    </div>
  );
}

// ============================================================
// Main — fully CMS-driven
// ============================================================
export default function PublicSite() {
  const [cms, setCms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openSvc, setOpenSvc] = useState(null);

  const fetchCms = useCallback(async () => {
    setError(null);
    try {
      // Cache-buster prevents browser/CDN from returning stale JSON after admin saves.
      const r = await axios.get(`${API}/public/site?t=${Date.now()}`, {
        headers: { "Cache-Control": "no-cache" },
      });
      setCms(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Failed to load content");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const line1 = cms?.brand?.line1 || "SHREE SAMARTH";
    const line2 = cms?.brand?.line2 || "CONSULTANCY";
    document.title = `${line1} ${line2} — ${cms?.brand?.tagline || "Legal Documentation & Property Registration"}`;
  }, [cms]);
  useEffect(() => {
    fetchCms();
  }, [fetchCms]);

  // Auto-refresh when user returns to the tab so admin edits appear without hard-refresh.
  useEffect(() => {
    const onFocus = () => fetchCms();
    const onVisibility = () => { if (document.visibilityState === "visible") fetchCms(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [fetchCms]);

  const catSvcs = useMemo(() => {
    const m = {};
    if (!cms) return m;
    for (const s of cms.services || []) (m[s.category_id] ||= []).push(s);
    for (const k of Object.keys(m)) m[k].sort((a, b) => (a.order || 0) - (b.order || 0));
    return m;
  }, [cms]);

  if (loading && !cms) return <LoadingSplash />;
  if (error && !cms) return <ErrorSplash onRetry={fetchCms} message={error} />;

  const offices = cms?.offices || [];
  const brand = cms?.brand || {};
  const categories = cms?.categories || [];
  const services = cms?.services || [];
  const primary = offices[0] || null;

  const nav = [
    { label: "Home", href: "#home" },
    { label: "About", href: "#about" },
    ...categories.map((c) => ({ label: c.name, href: `#${c.slug}` })),
    { label: "Why Us", href: "#why" },
    { label: "FAQ", href: "#faq" },
    { label: "Contact", href: "#contact" },
  ];

  return (
    <div className="bg-white text-slate-800 font-sans" data-testid="public-site">
      <Header nav={nav} brand={brand} primaryPhone={primary?.phone_tel} primaryPhoneDisplay={primary?.phone_display} />
      <Hero office={primary} brand={brand} />
      <About />
      {categories.map((c, i) => (
        <DynamicCategorySection
          key={c.id}
          cat={c}
          services={catSvcs[c.id] || []}
          onOpen={setOpenSvc}
          isDark={i % 3 === 2}
        />
      ))}
      <WhyUs />
      <Testimonials />
      <FAQ primaryPhoneDisplay={primary?.phone_display} />
      <Contact offices={offices} categories={categories} services={services} />
      <Footer offices={offices} nav={nav} brand={brand} />
      <FloatingButtons primaryOffice={primary} />
      {openSvc && <ServiceDetailModal svc={openSvc} primaryOffice={primary} brand={brand} onClose={() => setOpenSvc(null)} />}
    </div>
  );
}
